/**
 * "Verified at checkout" (Trust Trail) — views/store/_verification-feed.ejs.
 *
 * Merchant pages only; a page without the section is a no-op.
 *
 *   * DATES. The page is generated, not rendered per visit, so every
 *     `<time datetime>` ships an absolute date and is rewritten here as
 *     "today" / "1 day ago" / "N days ago" / "N months ago".
 *   * PROOF. Each row is a dropdown: clicking it (or its square screenshot
 *     preview, the `[data-gv-expand]` button) opens the full screenshot in the
 *     row's `.gv-panel` BELOW it, pushing the rest down. One open at a time.
 *     The full image ships as `data-src` and is fetched on first open.
 *   * SHOW ALL. Rows past the first few carry data-extra and show once the
 *     section is expanded.
 */
const DAY = 864e5;

// Days up to a month, then whole months: "today", "1 day ago", "29 days ago",
// "1 month ago", "2 months ago". Lowercase: every use is mid-sentence
// ("updated today", "Verified today by …", "verified by …, today").
function relativeLabel(days) {
  if (days === 0) return "today";
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

function relativeDates(feed) {
  feed.querySelectorAll("time[datetime]").forEach((t) => {
    const at = new Date(t.getAttribute("datetime")).getTime();
    if (Number.isNaN(at)) return;
    t.textContent = relativeLabel(Math.max(0, Math.floor((Date.now() - at) / DAY)));
  });
}

function initExpand(feed) {
  const panelOf = (trigger) => document.getElementById(trigger.getAttribute("aria-controls"));
  const triggerOf = (panel) => feed.querySelector(`[data-gv-expand][aria-controls="${panel.id}"]`);

  const setOpen = (trigger, open) => {
    const panel = panelOf(trigger);
    if (!panel) return;
    if (open) {
      // one open at a time
      feed.querySelectorAll('.gv-panel[data-open="true"]').forEach((p) => {
        if (p !== panel) {
          const t = triggerOf(p);
          if (t) setOpen(t, false);
        }
      });
      const img = panel.querySelector("img[data-src]");
      if (img && !img.getAttribute("src")) img.setAttribute("src", img.dataset.src);
    }
    panel.setAttribute("data-open", String(open));
    trigger.setAttribute("aria-expanded", String(open));
    trigger.setAttribute(
      "aria-label",
      (trigger.getAttribute("aria-label") || "").replace(/^(Show|Hide)/, open ? "Hide" : "Show"),
    );
    if (open) {
      // after the 0.3s expand, bring a screenshot that opened below the fold into view
      setTimeout(() => {
        if (panel.getBoundingClientRect().bottom > window.innerHeight) {
          panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 320);
    }
  };

  const toggle = (trigger) => setOpen(trigger, trigger.getAttribute("aria-expanded") !== "true");

  feed.addEventListener("click", (e) => {
    const hide = e.target.closest(".gv-hide");
    if (hide) {
      const trigger = triggerOf(hide.closest(".gv-panel"));
      if (trigger) {
        setOpen(trigger, false);
        trigger.focus();
      }
      return;
    }
    const trigger = e.target.closest("[data-gv-expand]");
    if (trigger) {
      toggle(trigger);
      return;
    }
    // the whole row header is the dropdown — except its own links/buttons and
    // the open screenshot itself
    const item = e.target.closest(".gv-item");
    if (item && !e.target.closest("a, button")) {
      const rowTrigger = item.querySelector("[data-gv-expand]");
      if (rowTrigger) toggle(rowTrigger);
    }
  });

  feed.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const panel = e.target.closest('.gv-panel[data-open="true"]');
    const trigger = panel && triggerOf(panel);
    if (trigger) {
      setOpen(trigger, false);
      trigger.focus();
    }
  });
}

function initShowAll(feed) {
  const btn = feed.querySelector("[data-gv-toggle]");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const expand = feed.getAttribute("data-expanded") !== "true";
    feed.setAttribute("data-expanded", String(expand));
    btn.setAttribute("aria-expanded", String(expand));
    btn.textContent = expand ? btn.dataset.labelLess : btn.dataset.labelMore;
  });
}

export default function initVerificationFeed() {
  const feed = document.querySelector(".gv-feed");
  if (!feed) return;

  relativeDates(feed);
  initExpand(feed);
  initShowAll(feed);
}
